/**
 * "My projects, boards & documents" on this device (T10 of "Track on this
 * device", 04.10.2026): the PERSONAL scope's folder as a LOCAL git history
 * with no remote — the scope twin of localGitSync.js (workspaces).
 *
 * - trackScopeLocally / saveScopeVersion: refresh the scope folder → secret
 *   scan → commit. Nothing goes to a network.
 * - addScopeRemote: the first-connect sequence on top of the existing history
 *   — create-if-missing → set the remote → fetch → refuse a repository whose
 *   apiboo.yaml is not THIS scope (another workspace, a team's boards &
 *   documents) → commit → push → on "diverged" merge (and apply) → push.
 *   Local commits are never rewritten.
 * - stopScopeTracking: the link and the local history go; the items stay.
 *
 * Team scopes are never local: a team's history must be shared.
 */
import { invoke } from '@tauri-apps/api/core';
import { getProvider, normalizeGitLink } from './providers';
import localProvider from './providers/local';
import { DEFAULT_BRANCH, branchOfLink, requireToken, credArgs, authorFor } from './gitAuth';
import { commitMessageFor } from './mirrorChanges';
import { scopeMirrorDir, refreshScopeMirror, saveScopeLink, isCurrentScope } from './scopeRepo';
import { ensureContributors, withScopeLock, scanScopeForSecrets, mergeAndApply } from './scopeGitSync';
import { teamIdOf } from './teamRepo';
import { APP_NAME } from '../../utils/appInfo';

export const LOCAL = 'local';
export const REMOTE_HAS_OTHER_SCOPE = 'REMOTE_HAS_OTHER_SCOPE';
export const TEAM_NOT_LOCAL = 'A team’s history must be shared — connect it to a server';

export const isLocalScopeLink = (git) => git?.provider === LOCAL;

/** A fresh local link: no owner, repo, url, account or Vault item. */
const localScopeLink = (since = Date.now()) => ({ provider: LOCAL, branch: DEFAULT_BRANCH, since });

function requireLocalable(scope) {
  if (teamIdOf(scope?.key)) throw new Error(TEAM_NOT_LOCAL);
  if (!isCurrentScope(scope?.key)) throw new Error('This link belongs to another account. Connect again while signed in as it.');
}

/** Refresh the scope folder and commit it (scan first). Caller holds the scope lock. */
async function commitScope(scope, { allowSecrets, message }) {
  const dir = await scopeMirrorDir(scope.key);
  await ensureContributors();
  await invoke('git_init_or_open', { repoPath: dir, branch: DEFAULT_BRANCH });
  await invoke('git_merge_abort', { repoPath: dir });
  await refreshScopeMirror(scope.key, scope.name);
  if (!allowSecrets) {
    const findings = await scanScopeForSecrets(dir, scope.key);
    if (findings.length > 0) return { status: 'secrets', findings };
  }
  const author = await authorFor(localProvider, scope.git);
  const commit = await invoke('git_commit_all', { repoPath: dir, message: await commitMessageFor(dir, message, 'scope'), ...author });
  return { status: 'ok', commit: commit || null };
}

/**
 * "This device only (no remote)" for the personal scope: its first version.
 * @returns {Promise<{status:'ok'|'secrets', git:object, commit?, findings?}>}
 */
export async function trackScopeLocally(scope, { allowSecrets = false } = {}) {
  requireLocalable(scope);
  return withScopeLock(scope.key, async () => {
    const git = { ...localScopeLink(), ...(allowSecrets ? { secretsAllowed: true } : {}) };
    const result = await commitScope({ ...scope, git }, { allowSecrets, message: `Start tracking in ${APP_NAME}` });
    if (result.status === 'secrets') return { ...result, git };
    await saveScopeLink(scope.key, git);
    return { status: 'ok', git, commit: result.commit, local: true };
  });
}

/** "Save version": commit what the scope holds now (`commit` null = nothing new). */
export async function saveScopeVersion(scope, { allowSecrets = false } = {}) {
  if (!isLocalScopeLink(scope?.git)) throw new Error('NOT_LOCAL');
  requireLocalable(scope);
  return withScopeLock(scope.key, async () => {
    const result = await commitScope(scope, { allowSecrets, message: `Update ${scope.name} from ${APP_NAME}` });
    if (result.status !== 'ok') return { ...result, git: scope.git };
    let git = scope.git;
    if (allowSecrets && !git.secretsAllowed) {
      git = { ...git, secretsAllowed: true };
      await saveScopeLink(scope.key, git);
    }
    return { ...result, git, local: true };
  });
}

/**
 * Whether a manifest (apiboo.yaml) is THIS scope's: `apiboo: scope` and the
 * same `scope:` kind (and `team:` id for a team). A workspace manifest, a
 * team's boards & documents for the personal scope — not.
 */
export function isScopeManifestFor(text, scopeKey) {
  if (typeof text !== 'string' || !/^apiboo:[^\S\r\n]*scope[^\S\r\n]*$/m.test(text)) return false;
  const kind = /^scope:[^\S\r\n]*(\S+)[^\S\r\n]*$/m.exec(text)?.[1];
  const team = /^team:[^\S\r\n]*["']?([^"'\r\n]*?)["']?[^\S\r\n]*$/m.exec(text)?.[1] || null;
  const teamId = teamIdOf(scopeKey);
  if (teamId) return kind === 'team' && team === teamId;
  return kind === 'personal' && team == null;
}

function refusal() {
  const err = new Error(
    'That repository already holds something else (a workspace or a team’s projects, boards & documents). Choose a new or empty repository.'
  );
  err.code = REMOTE_HAS_OTHER_SCOPE;
  return err;
}

/** Throws when the fetched branch carries an apiboo.yaml that is not this scope's. No apiboo.yaml (README) is fine. */
async function checkRemoteScope(dir, scope, branch) {
  const st = await invoke('git_status', { repoPath: dir, remote: 'origin', branch });
  if (!st?.remoteOid) return;
  const theirs = await invoke('git_file_at', { repoPath: dir, oid: st.remoteOid, path: 'apiboo.yaml' });
  if (theirs == null) return;
  if (!isScopeManifestFor(theirs, scope.key)) throw refusal();
}

/**
 * Add a remote to the personal scope tracked on this device and push its whole
 * history (the same commit ids, plus a merge when the remote already had work).
 * @returns {Promise<{status:'ok'|'secrets'|'conflicts'|'diverged'|'not_applied', git:object, merged?, applied?, findings?, conflicts?}>}
 *   `git` is the new REMOTE link on 'ok' / 'conflicts', the unchanged local link
 *   otherwise. On REMOTE_HAS_OTHER_SCOPE (thrown) the remote is removed again
 *   and nothing was pushed.
 */
export async function addScopeRemote(
  scope,
  { owner, repo, createIfMissing = true, isPrivate = true, allowSecrets = false, provider: providerId, accountId, url: remoteUrl, vaultItemId, vaultItemName } = {}
) {
  if (!isLocalScopeLink(scope?.git)) throw new Error('NOT_LOCAL');
  requireLocalable(scope);
  return withScopeLock(scope.key, async () => {
    const provider = getProvider(providerId);
    if (provider.localOnly) throw new Error('Choose GitHub, GitLab or another git server.');
    const ctx = { provider: provider.id, accountId, url: remoteUrl, vaultItemId, vaultItemName };
    const token = await requireToken(provider, ctx);
    if (!owner || !repo) throw new Error('Repository owner and name are required');

    const dir = await scopeMirrorDir(scope.key);
    const branch = branchOfLink(scope.git);
    await ensureContributors();
    // The latest state is scanned before anything leaves this device.
    await invoke('git_init_or_open', { repoPath: dir, branch });
    await invoke('git_merge_abort', { repoPath: dir });
    await refreshScopeMirror(scope.key, scope.name);
    if (!allowSecrets) {
      const findings = await scanScopeForSecrets(dir, scope.key);
      if (findings.length > 0) return { status: 'secrets', findings, git: scope.git };
    }

    if (createIfMissing && provider.abilities.createRepo) await provider.ensureRepoExists(token, owner, repo, isPrivate, ctx);
    const author = await authorFor(provider, ctx);
    const url = await provider.repoUrl(owner, repo, ctx);
    const cred = credArgs(provider, token, ctx);
    const git = normalizeGitLink(
      {
        owner,
        repo,
        branch,
        url,
        provider: provider.id,
        ...(vaultItemId ? { vaultItemId, vaultItemName: vaultItemName || '' } : {}),
        ...(scope.git.since ? { since: scope.git.since } : {}),
      },
      { accountId: accountId || author.authorName || undefined }
    );

    await invoke('git_set_remote', { repoPath: dir, name: 'origin', url });
    const dropRemote = () => invoke('git_remove_remote', { repoPath: dir, name: 'origin' }).catch(() => {});
    try {
      // An empty repository has no branch to fetch; the check then runs before any merge.
      let checked = false;
      try {
        await invoke('git_fetch', { repoPath: dir, ...cred, remote: 'origin', branch });
        await checkRemoteScope(dir, scope, branch);
        checked = true;
      } catch (e) {
        if (e?.code === REMOTE_HAS_OTHER_SCOPE) throw e;
      }
      await invoke('git_commit_all', { repoPath: dir, message: await commitMessageFor(dir, `Update ${scope.name} from ${APP_NAME}`, 'scope'), ...author });

      let push = await invoke('git_push', { repoPath: dir, ...cred, remote: 'origin', branch });
      if (push.status !== 'diverged') {
        await saveScopeLink(scope.key, git);
        return { status: 'ok', git, merged: false };
      }
      if (!checked) {
        await invoke('git_fetch', { repoPath: dir, ...cred, remote: 'origin', branch });
        await checkRemoteScope(dir, scope, branch);
      }
      // The remote's own commits (a README, this scope from another device) join as a merge — applied to the app.
      await saveScopeLink(scope.key, git);
      const merged = await mergeAndApply({ ...scope, git }, dir, { ...cred, remote: 'origin', branch, ...author });
      if (merged.status === 'conflicts') return { ...merged, git };
      if (merged.status === 'not_applied') {
        await saveScopeLink(scope.key, scope.git);
        await dropRemote();
        return { ...merged, git: scope.git };
      }
      push = await invoke('git_push', { repoPath: dir, ...cred, remote: 'origin', branch });
      if (push.status !== 'ok') {
        await saveScopeLink(scope.key, scope.git);
        await dropRemote();
        return { status: 'diverged', git: scope.git };
      }
      return { status: 'ok', git, merged: merged.status !== 'up_to_date', applied: merged.applied };
    } catch (err) {
      // Anything that did not end in a push leaves the history local, as it was.
      await saveScopeLink(scope.key, scope.git).catch(() => {});
      await dropRemote();
      throw err;
    }
  });
}

/** How many versions the local history holds (null when it cannot be read). */
export async function countScopeVersions(scope) {
  try {
    const list = await invoke('git_history', { repoPath: await scopeMirrorDir(scope.key), path: null, limit: 1000 });
    return Array.isArray(list) ? list.length : null;
  } catch {
    return null;
  }
}

/** "Stop tracking": the link and the local history (the folder) go; the items in the app stay. */
export async function stopScopeTracking(scope) {
  if (!isLocalScopeLink(scope?.git)) throw new Error('NOT_LOCAL');
  return withScopeLock(scope.key, async () => {
    await saveScopeLink(scope.key, null);
    const fs = await import('@tauri-apps/plugin-fs');
    try {
      await fs.remove(await scopeMirrorDir(scope.key), { recursive: true });
    } catch {
      /* already gone — nothing left to delete */
    }
  });
}
